import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Rating,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import EditReviewDialog from '../components/EditReviewDialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { isReviewEdited } from '../utils/isEdited';
import EditedTag from '../components/EditedTag';
import {
  fetchMyDepartments,
  fetchMyCourses,
  fetchMyProfessorCourses,
  fetchMyReviews,
  updateReview,
  updateDepartment,
  updateCourse,
  updateProfessorCourse,
  deleteDepartment,
  deleteCourse,
  deleteReview,
} from '../services/api';
import IconTextChip from '../components/IconTextChip';
import IconOnlyChip from '../components/IconOnlyChip';
import TextOnlyChip from '../components/TextOnlyChip';
import SubmissionSearchBar from '../components/SubmissionSearchBar';
import SubmissionsFilterSidebar from '../components/SubmissionsFilterSidebar';
import UserSubmissionsSidebar from '../components/UserSubmissionsSidebar';
import RightSidebarContainer from '../components/RightSidebarContainer';
import CollapsibleSidebarSection from '../components/CollapsibleSidebarSection';
import EditDepartmentDialog from '../components/EditDepartmentDialog';
import EditCourseDialog from '../components/EditCourseDialog';
import EditProfessorCourseDialog from '../components/EditProfessorCourseDialog';
import FilledButton from '../components/FilledButton';
import OutlinedButton from '../components/OutlinedButton';
import { useAuth } from '../components/AuthContext';
import { useInstitution } from '../components/InstitutionContext';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VerifyEmailPromptAlert from '../components/VerifyEmailPromptAlert';
import NoPermissionPage from './NoPermissionPage';

const timeAgo = (dateString) => {
  const now = new Date();
  const past = new Date(dateString);
  const diff = Math.floor((now - past) / 1000);
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} months ago`;
  return `${Math.floor(diff / 31536000)} years ago`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function CoursesUserSubmissionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const previousPath = location.state?.from || '/courses';
  const { user, emailVerified } = useAuth();
  const unverifiedUser = user && !emailVerified;
  const { selectedInstitution } = useInstitution();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [links, setLinks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [tab, setTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showResubmitted, setShowResubmitted] = useState(false);
  const [showAccepted, setShowAccepted] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [showLocked, setShowLocked] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [showReported, setShowReported] = useState(false);
  const [editDepartment, setEditDepartment] = useState(null);
  const [editCourse, setEditCourse] = useState(null);
  const [editProfessorCourse, setEditProfessorCourse] = useState(null);
  const [editReview, setEditReview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const loadSubmissions = async () => {
    if (!user) return;
    setLoadingData(true);
    try {
      const instId = selectedInstitution || user?.userprofile?.institution?.id;
      const [d, c, l, r] = await Promise.all([
        fetchMyDepartments(instId),
        fetchMyCourses(instId),
        fetchMyProfessorCourses(instId),
        fetchMyReviews(instId),
      ]);
      setDepartments(d || []);
      setCourses(c || []);
      setLinks(l || []);
      setReviews(r || []);
    } catch (err) {
      console.error('Failed to load submissions', err);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadSubmissions();
    }
  }, [user]);

  if (!user) {
    return <NoPermissionPage />;
  }

  const combined = [
    ...departments.map((d) => ({ ...d, __type: 'department' })),
    ...courses.map((c) => ({ ...c, __type: 'course' })),
    ...links.map((l) => ({ ...l, __type: 'link' })),
    ...reviews.map((r) => ({ ...r, __type: 'review' })),
  ];

  const firstDate = combined.reduce((earliest, item) => {
    const ts = item.date_created || item.created_at;
    if (!ts) return earliest;
    const d = new Date(ts);
    return earliest && earliest < d ? earliest : d;
  }, null);

  let filtered = tab === 'all' ? combined : combined.filter((i) => i.__type === tab);

  if (tab === 'review') {
    if (showLocked || showRemoved || showReported) {
      filtered = filtered.filter((item) => {
        const locked = item.is_locked;
        const removed = item.state === 'REMOVED' || item.is_removed;
        const reported = item.state === 'REPORTED' || (item.report_count || 0) > 0;
        return (
          (showLocked && locked) ||
          (showRemoved && removed) ||
          (showReported && reported)
        );
      });
    }
  } else {
    if (showAccepted || showPending || showRejected || showResubmitted) {
      filtered = filtered.filter((item) => {
        let approved;
        let rejected;
        let resub;
        if (item.__type === 'review') {
          if (item.professor_course_is_approved !== undefined) {
            approved = item.professor_course_is_approved;
          } else {
            approved = true;
          }
          rejected = false;
          resub = false;
        } else {
          approved = item.is_approved;
          rejected = item.is_rejected;
          resub =
            !approved &&
            !rejected &&
            (item.resubmission_count > 0 ||
              (item.previous_submission &&
                Object.keys(item.previous_submission).length > 0));
        }
        const pending = !approved && !rejected && !resub;
        return (
          (showAccepted && approved) ||
          (showPending && pending) ||
          (showRejected && rejected) ||
          (showResubmitted && resub)
        );
      });
    }
  }

  if (searchQuery.trim() !== '') {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter((item) => JSON.stringify(item).toLowerCase().includes(q));
  }

  filtered.sort((a, b) => {
    if (tab === 'review' && sortBy === 'liked') {
      const la = (a.upvotes || 0) - (a.downvotes || 0);
      const lb = (b.upvotes || 0) - (b.downvotes || 0);
      return lb - la;
    }
    const da = new Date(a.date_created || a.created_at);
    const db = new Date(b.date_created || b.created_at);
    return sortBy === 'oldest' ? da - db : db - da;
  });

  const filters =
    tab === 'review'
      ? [
          { label: 'Reported', value: showReported, toggle: setShowReported },
          { label: 'Locked', value: showLocked, toggle: setShowLocked },
          { label: 'Removed', value: showRemoved, toggle: setShowRemoved },
        ]
      : [
          { label: 'Approved', value: showAccepted, toggle: setShowAccepted },
          { label: 'Pending', value: showPending, toggle: setShowPending },
          { label: 'Rejected', value: showRejected, toggle: setShowRejected },
          { label: 'Resubmitted', value: showResubmitted, toggle: setShowResubmitted },
        ];

  const handleEditItem = (item) => {
    if (item.__type === 'department') {
      setEditDepartment(item);
      return;
    }
    if (item.__type === 'course') {
      setEditCourse(item);
      return;
    }
    if (item.__type === 'link') {
      setEditProfessorCourse(item);
      return;
    }
    if (item.__type === 'review') {
      setEditReview(item);
      return;
    }
  };

  const handleDeleteItem = (item) => {
    setConfirmDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.__type === 'department') {
        await deleteDepartment(confirmDelete.id);
      } else if (confirmDelete.__type === 'course') {
        await deleteCourse(confirmDelete.id);
      } else if (confirmDelete.__type === 'review') {
        await deleteReview(confirmDelete.id);
      }
      await loadSubmissions();
    } catch (err) {
      console.error('Failed to delete submission', err);
    } finally {
      setConfirmDelete(null);
    }
  };


  const handleResubmitDepartment = async (id, data) => {
    try {
      await updateDepartment(id, data);
      await loadSubmissions();
      setSuccessMessage('Department updated.');
    } catch (err) {
      console.error('Failed to edit entry', err);
    } finally {
      setEditDepartment(null);
    }
  };

  const handleResubmitCourse = async (id, data) => {
    try {
      await updateCourse(id, data);
      await loadSubmissions();
      setSuccessMessage('Course updated.');
    } catch (err) {
      console.error('Failed to edit entry', err);
    } finally {
      setEditCourse(null);
    }
  };

  const handleReviewUpdated = async () => {
    try {
      await loadSubmissions();
    } catch (err) {
      console.error('Failed to update review', err);
    } finally {
      setEditReview(null);
    }
  };

  const handleResubmitProfCourse = async (id, data) => {
    try {
      await updateProfessorCourse(id, data);
      await loadSubmissions();
      setSuccessMessage('Professor-course updated.');
    } catch (err) {
      console.error('Failed to edit entry', err);
    } finally {
      setEditProfessorCourse(null);
    }
  };

  const renderCard = (item) => {
    const t = item.__type;
    let header = '';
    let title = '';
    let body = null;
    if (t === 'department') {
      header = 'Department';
      title = item.name;
    } else if (t === 'course') {
      header = 'Course';
      title = item.department?.name || '';
      body = (
      <Typography>
          {item.short_name} – {item.long_name}
      </Typography>
      );
    } else if (t === 'professor') {
      header = 'Professor';
      title =
        item.course_department?.name ||
        item.courses_info?.[0]?.department?.name ||
        '';
    } else if (t === 'link') {
      header = 'Professor';
      title = (
        <>
          {item.course?.short_name} – {item.course?.long_name} {item.course?.department?.name && (
            <>({item.course.department.name})</>
          )}
        </>
      );
      body = (
        <Typography>{
          item.professor?.name || item.professor_submission?.name
        }</Typography>
      );
    } else if (t === 'review') {
      header = 'Review';
      const profName =
        item.professor_name ||
        (typeof item.professor === 'object' ? item.professor?.name : item.professor);
      const courseData = item.course || {};
      const short =
        item.course_name ||
        (typeof courseData === 'object' ? courseData.short_name : courseData);
      const long =
        item.course_long_name ||
        (typeof courseData === 'object' ? courseData.long_name : '');
      const sem = item.semester
        ? `${item.semester.term} ${item.semester.year}`
        : '';
      title = [profName, short, long, sem].filter(Boolean).join(' – ');
      body = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {profName}
          </Typography>
          <Rating value={item.rating || 0} readOnly size="small" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Difficulty:
            </Typography>
            <Typography sx={{ fontWeight: 'bold', mr: 2 }}>
              {item.difficulty_level} / 5
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Grade:
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }}>
              {item.grade_received !== null && item.grade_received !== undefined
                ? item.grade_received
                : '-'}
            </Typography>
          </Box>
          <Typography>
            {item.content}
            {isReviewEdited(item) && <EditedTag />}
          </Typography>
        </Box>
      );
    }
    let status = '';
    let statusColor = 'warning';
    if (t === 'review') {
      if (item.is_locked) {
        status = 'Locked';
      } else if (item.state === 'REMOVED' || item.is_removed) {
        status = 'Removed';
      } else if (item.state === 'REPORTED' || item.report_count > 0) {
        status = 'Reported';
      } else {
        status = '';
      }
    } else if (item.is_rejected) {
      status = 'Rejected';
      statusColor = 'error';
    } else if (item.is_approved) {
      status = 'Approved';
      statusColor = 'info';
    } else if (
      item.resubmission_count > 0 ||
      (item.previous_submission && Object.keys(item.previous_submission).length > 0)
    ) {
      status = 'Resubmitted';
    } else {
      status = 'Pending Approval';
    }

    return (
      <Card
        key={`${t}-${item.id}`}
        sx={{ mb: 2, p: 0, border: '1px solid #ddd', boxShadow: 'none', '&:hover': { boxShadow: 2 } }}
      >
        <Box sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} color="grey.400">
            {header}
          </Typography>
        </Box>
        <CardContent sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt:-1, mb:-0.5}}>
            <Typography sx={{ flex: 1, wordBreak: 'break-word', fontWeight: 'bold' }}>
              {title}
            </Typography>
            {status && <TextOnlyChip label={status} color={statusColor} />}
          </Box>
          {body && (
            <Box sx={{ wordBreak: 'break-word' }}>
              {body}
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {(item.date_created || item.created_at) && (
              <IconTextChip
                icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                label={(() => {
                  const ts = item.date_created || item.created_at;
                  return new Date() - new Date(ts) > 86400000
                    ? formatDate(ts)
                    : timeAgo(ts);
                })()}
                color="default"
              />
            )}
            {t === 'review' && (
              <>
                <IconTextChip
                  icon={<ThumbUpOffAltIcon />}
                  label={item.upvotes || 0}
                  color="info"
                  disabled={loadingData}
                />
                <IconTextChip
                  icon={<ThumbDownOffAltIcon />}
                  label={item.downvotes || 0}
                  color="info"
                  disabled={loadingData}
                />
              </>
            )}
            {(
              t === 'review' ||
              (['department', 'course', 'link'].includes(t) && item.is_rejected)
            ) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                {t === 'review' && (
                  <>
                    <IconOnlyChip
                      icon={<EditOutlinedIcon />}
                      color="info"
                      onClick={() => handleEditItem(item)}
                      disabled={unverifiedUser}
                    />
                    <IconOnlyChip
                      icon={<DeleteOutlineOutlinedIcon />}
                      color="error"
                      onClick={() => handleDeleteItem(item)}
                      disabled={unverifiedUser}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ maxWidth: '1200px', width: '100%', mx: 'auto', py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, px: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon color="info" />}
            onClick={() => navigate(previousPath)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>

          <Typography variant="h4" gutterBottom>
            My Submissions
          </Typography>

          {unverifiedUser && (
            <VerifyEmailPromptAlert
              message="to manage your submissions."
              sx={{ mb: 2 }}
            />
          )}

          {isSmallScreen ? (
            <>
              <CollapsibleSidebarSection title={user?.username || ''}>
                <UserSubmissionsSidebar
                  username={user?.username}
                  count={combined.length}
                  firstDate={firstDate}
                />
              </CollapsibleSidebarSection>
              <CollapsibleSidebarSection title="Filters">
                <SubmissionsFilterSidebar filters={filters} />
              </CollapsibleSidebarSection>
            </>
          ) : null}

          {isSmallScreen ? (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mb: 2 }}
              >
                <SubmissionSearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl size="small">
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    {tab === 'review' && (
                      <MenuItem value="liked">Most Liked</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Tabs
                  value={tab}
                  onChange={(e, val) => setTab(val)}
                  variant="fullWidth"
                  TabIndicatorProps={{ sx: { backgroundColor: 'info.main' } }}
                >
                  <Tab
                    label="All"
                    value="all"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                  <Tab
                    label="Departments"
                    value="department"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                  <Tab
                    label="Courses"
                    value="course"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                  <Tab
                    label="Professors"
                    value="link"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                  <Tab
                    label="Reviews"
                    value="review"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                </Tabs>
              </Box>
            </>
          ) : (
            <Box
              sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 2, gap: 2 }}
            >
              <Tabs
                value={tab}
                onChange={(e, val) => setTab(val)}
                TabIndicatorProps={{ sx: { backgroundColor: 'info.main' } }}
              >
                <Tab
                  label="All"
                  value="all"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
                <Tab
                  label="Departments"
                  value="department"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
                <Tab
                  label="Courses"
                  value="course"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
                <Tab
                  label="Professors"
                  value="link"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
                <Tab
                  label="Reviews"
                  value="review"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
              </Tabs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
                <SubmissionSearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <FormControl size="small">
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    {tab === 'review' && (
                      <MenuItem value="liked">Most Liked</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {filtered.map((item) => renderCard(item))}
        </Box>

        {!isSmallScreen && (
          <RightSidebarContainer sticky topOffset={64} sx={{ ml: 3, mt: 6 }}>
            <UserSubmissionsSidebar
              username={user?.username}
              count={combined.length}
              firstDate={firstDate}
            />
            <SubmissionsFilterSidebar filters={filters} />
          </RightSidebarContainer>
        )}
      </Box>
      <EditDepartmentDialog
        open={Boolean(editDepartment)}
        department={editDepartment}
        onClose={() => setEditDepartment(null)}
        onResubmit={handleResubmitDepartment}
      />
      <EditCourseDialog
        open={Boolean(editCourse)}
        course={editCourse}
        onClose={() => setEditCourse(null)}
        onResubmit={handleResubmitCourse}
      />
      <EditProfessorCourseDialog
        open={Boolean(editProfessorCourse)}
        link={editProfessorCourse}
        onClose={() => setEditProfessorCourse(null)}
        onResubmit={handleResubmitProfCourse}
      />
      <EditReviewDialog
        open={Boolean(editReview)}
        review={editReview}
        onClose={() => setEditReview(null)}
        onSaved={(data) => handleReviewUpdated(editReview.id, data)}
      />
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Submission?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this submission?</Typography>
        </DialogContent>
        <DialogActions>
          <OutlinedButton onClick={() => setConfirmDelete(null)} text="Cancel" color="success" />
          <FilledButton onClick={handleConfirmDelete} text="Delete" color="error" />
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
